import { Divider, Typography } from '@material-ui/core';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { StatusIcon } from 'framework/components/icons/StatusIcon';
import { CaptionVerticalLabelledPropertyX } from 'framework/components/labelledProperty/CaptionVerticalLabelledPropertyX';
import { Typography16pxBold } from 'framework/components/typography/Typography16pxBold';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { disablePeppolIntegration_handle, enablePeppolIntegration_handle, IPeppolIntegrationStatus } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { PatchPeppolSettingsDialog } from './peppol.components';

type PeppolStatusTabProps = {
	status: IPeppolIntegrationStatus;
	reload: VoidFunction;
};

export const PeppolStatusTab = ({ status, reload }: PeppolStatusTabProps) => {
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);

	const onEnable = () => {
		open(
			<AlertDialog
				open
				title={strings.activateIntegration}
				content={strings.doYouWantToActivateThisIntegrationQuestion}
				acceptText={strings.yesCommaActivate}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => enablePeppolIntegration_handle({ empty: '' })}
				accept={confirm}
			/>
		);
	};
	const onDisable = () => {
		open(
			<AlertDialog
				open
				title={strings.deactivateIntegration}
				content={strings.doYouWantToDeactivateThisIntegrationQuestion}
				acceptText={strings.yesCommaDeactivate}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => disablePeppolIntegration_handle({ empty: '' })}
				accept={confirm}
			/>
		);
	};

	const onPatchSettings = () => {
		open(
			<PatchPeppolSettingsDialog
				open
				cancel={cancel}
				confirm={confirm}
				settings={status.peppolSettings}
			/>
		);
	};

	return (
		<div
			className='df-col gap-16 fg1'
			style={{ padding: 16, maxWidth: 1280 }}>
			<CardX className='df-col gap-8'>
				<Typography16pxBold>{strings.peppolCompatibility}</Typography16pxBold>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.ibanFilledIn}?`}
					startIcon={<StatusIcon status={status.hasIban ? 'success' : 'error'} />}>
					{status.iban}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.bicFilledIn}?`}
					startIcon={<StatusIcon status={status.hasBic ? 'success' : 'error'} />}>
					{status.bic}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.enterpriseNumberFilledIn}?`}
					startIcon={<StatusIcon status={status.hasEnterpriseNumber ? 'success' : 'error'} />}>
					{status.enterpriseNumber}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.vatNumberFilledIn}?`}
					startIcon={<StatusIcon status={status.hasVatNumber ? 'success' : 'error'} />}>
					{status.vatNumber}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.enterpriseNumberAndVatNumberMatchQuestion}`}
					startIcon={<StatusIcon status={status.mismatchEnterpriseNumberAndVatNumber ? 'error' : 'success'} />}>
					{status.mismatchEnterpriseNumberAndVatNumber ? strings.no : strings.yes}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`${strings.registrationOnHams}?`}
					startIcon={<StatusIcon status={status.mismatchEnterpriseNumberAndRootEndpointId ? 'error' : 'success'} />}>
					<>
						{status.mismatchEnterpriseNumberAndRootEndpointId === true && (
							<Typography style={{ fontStyle: 'italic', fontSize: 14, color: 'red' }}>{strings.contactSupportForMoreInformation}</Typography>
						)}
						{status.mismatchEnterpriseNumberAndRootEndpointId === false && status.rootEndpointId}
					</>
				</CaptionVerticalLabelledPropertyX>
			</CardX>
			<CardX className='df-col gap-8'>
				<Typography16pxBold>{strings.settings}</Typography16pxBold>
				<CaptionVerticalLabelledPropertyX
					label={strings.status}
					startIcon={<StatusIcon status={status.peppolSettings.enabled ? 'success' : 'error'} />}>
					{status.peppolSettings.enabled ? strings.activated : strings.notActived}
				</CaptionVerticalLabelledPropertyX>
				<CaptionVerticalLabelledPropertyX
					label={`Environment`}
					startIcon={<StatusIcon status={status.environment === 'Production' ? 'success' : 'warning'} />}>
					{status.environment}
				</CaptionVerticalLabelledPropertyX>
				<TextLinkButton
					startIcon={<CheckBoxOutlinedIcon />}
					onClick={onEnable}
					color='primary'
					disabled={status.peppolSettings.enabled === true}>
					{strings.activateVerb}
				</TextLinkButton>
				<TextLinkButton
					startIcon={<HighlightOffOutlinedIcon />}
					onClick={onDisable}
					color='secondary'
					hoverColor='secondary'
					disabled={status.peppolSettings.enabled === false}>
					{strings.deactivateVerb}
				</TextLinkButton>
				<Divider />
				<CaptionVerticalLabelledPropertyX
					label={`${strings.autoSend}?`}
					startIcon={<StatusIcon status={status.peppolSettings.autoSend ? 'success' : 'error'} />}
					edit={onPatchSettings}>
					{status.peppolSettings.autoSend ? strings.invoicesWillBeAutomaticallyForwardedToPeppol : strings.invoicesWillNotBeAutomaticallyForwardedToPeppol}
				</CaptionVerticalLabelledPropertyX>
				<Divider />
				<CaptionVerticalLabelledPropertyX
					label={`${strings.withOrWithoutDocumentAutoForward}?`}
					startIcon={<StatusIcon status={status.peppolSettings.waitForDocument ? 'success' : 'error'} />}
					edit={onPatchSettings}>
					{status.peppolSettings.waitForDocument ? strings.createDocumentFirstThanForward : strings.forwardWithoutDocument}
				</CaptionVerticalLabelledPropertyX>
			</CardX>
		</div>
	);
};
